/* @jsx h */
import { defineComponent, ref, h, onMounted } from "vue";
import { RouterLink } from "vue-router";
import { TableStatus } from "@lemmonhq/types";
import { TableDetails } from "./TableDetails";
import { cn, useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

import { TableNotesModal } from "./TableNotesModal";
import { useTableDetailsActions } from "./useTableDetailsActions";
import { useMutation } from "@tanstack/vue-query";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { TableAmountLines } from "@app/vue/components/TableCredit";

export type TableRowProps = {
  tableStatus: TableStatus;
  onTablePayment: Function;
  view: "desktop" | "mobile";
  onShowQR: (tableId: number) => void;
};

export const TableRow = defineComponent<TableRowProps>({
  name: "TableRow",
  props: ["tableStatus", "onTablePayment", "view", "onShowQR"],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const isPrinting = ref(false);
    const isNotesModalOpen = ref(false);
    const isTogglingStatus = ref(false);
    const toast = useToast();
    const trpc = useTrpc();
    const toggleBusyMutation = useMutation({
      mutationFn: (input: { table_id: number; current_status: number }) =>
        trpc.tables.toggleBusy.mutate(input),
    });

    const currentNotes = ref("");
    const newNotes = ref("");

    const isBottomSheetOpen = ref(false);
    const tableDetailsActions = useTableDetailsActions({
      getTableStatus: () => props.tableStatus,
      onTableUpdated: () => props.onTablePayment(),
    });

    const getTimeSinceFirstOrder = () => {
      if (props.tableStatus.orders.length === 0) return "-";

      const firstOrderDate = new Date(
        Math.min(
          ...props.tableStatus.orders.map((order) =>
            new Date(order.created_at).getTime(),
          ),
        ),
      );
      const now = new Date();
      const diffInMinutes = Math.floor(
        (now.getTime() - firstOrderDate.getTime()) / (1000 * 60),
      );

      if (diffInMinutes < 60) {
        return `${diffInMinutes}m`;
      }
      const hours = Math.floor(diffInMinutes / 60);
      const minutes = diffInMinutes % 60;
      return `${hours}h ${minutes}m`;
    };

    const getTableStatusIcon = (isBusy: boolean) => {
      if (isBusy) {
        return (
          <span class="material-symbols-outlined text-red-500 text-2xl">
            group
          </span>
        );
      }
      return (
        <span class="material-symbols-outlined text-green-500 text-2xl">
          self_improvement
        </span>
      );
    };

    onMounted(() => {
      if (props.tableStatus.party?.name) {
        currentNotes.value = props.tableStatus.party?.name;
      } else {
        currentNotes.value = "";
      }
    });

    const handleToggleTableStatus = async () => {
      window.alert("Feature disabled");
      return;

      if (import.meta.env.VITE_ENV === "production") {
        return;
      }
      try {
        isTogglingStatus.value = true;
        const currentStatus = props.tableStatus.table.is_busy ? 1 : 0;
        await toggleBusyMutation.mutateAsync({
          table_id: props.tableStatus.table.id,
          current_status: currentStatus,
        });
        toast.showToast(
          currentStatus === 1 ? "Table marked as free" : "Table marked as busy",
          {
            type: "success",
            autoHide: 3000,
            icon: "table_restaurant",
          },
        );
        props.onTablePayment();
      } catch (error) {
        console.error("Failed to toggle table status:", error);
        toast.showToast("Failed to update table status", {
          type: "error",
          autoHide: 3000,
          icon: "error",
        });
      } finally {
        isTogglingStatus.value = false;
      }
    };

    const renderActionButtons = (isBusy: boolean) => (
      <div
        class={`gap-2 flex ${
          props.view === "mobile" ? "flex-wrap" : "justify-end"
        }`}
      >
        <div class={cn("dropdown dropdown-left flex items-center")}>
          <div tabindex="0" role="button" class="">
            <span class="material-symbols-outlined text-4xl">more_vert</span>
          </div>
          <ul
            tabindex="0"
            class={cn(
              "dropdown-content text-accent menu l-box-shadow bg-base-200 border border-black rounded rounded-lg z-[10] w-52 p-0 shadow",
            )}
          >
            <li>
              <a
                class="py-4 text-1xl"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  console.log("changing table");
                  tableDetailsActions.showChangeTableModal.value = true;
                }}
              >
                {t("tables.general.change_table")}
              </a>
            </li>
            <li>
              <a
                class="py-4 text-1xl"
                href="#"
                onClick={(e) => {
                  //                  e.preventDefault();
                  console.log(e.currentTarget);
                  tableDetailsActions.showTransferItemsModal.value = true;
                }}
              >
                Reassign Items
              </a>
            </li>
            <li>
              <a
                class="py-4 text-1xl"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  props.onShowQR(props.tableStatus.id);
                  (e.currentTarget as HTMLAnchorElement).blur();
                }}
              >
                Show QR
              </a>
            </li>
          </ul>
        </div>
        {isBusy ? (
          <>
            <button
              class="btn btn-secondary "
              disabled={isPrinting.value}
              onClick={async (e) => {
                e.preventDefault();
                try {
                  isPrinting.value = true;
                  await tableDetailsActions.printReceipt();
                } catch (e) {
                  console.error("Failed to print bill:", e);
                  //                      toast.showToast("Failed to print bill", {
                  //                        type: "error",
                  //                        icon: "print_error",
                  //                      });
                } finally {
                  isPrinting.value = false;
                }
              }}
            >
              <span class=" material-symbols-outlined ml-1 text-2xl">
                print
              </span>
            </button>
            <RouterLink
              class="btn btn-accent "
              to={`/app/pos?tableId=${props.tableStatus.table.id}`}
            >
              {() => (
                <span class="flex justify-center items-center">
                  <span class="material-symbols-outlined mr-0.5">
                    add_shopping_cart
                  </span>{" "}
                  <span class="hidden 2xl:inline">
                    {t("tables.general.add_button")}
                  </span>
                </span>
              )}
            </RouterLink>
            <RouterLink
              class="btn btn-neutral "
              to={`/app/orders?tableId=${props.tableStatus.table.id}`}
            >
              {() => (
                <span class="flex justify-center items-center">
                  <span class="material-symbols-outlined mr-0.5">
                    order_approve
                  </span>
                  <span class="hidden 2xl:inline">
                    {t("tables.general.orders_button")}
                  </span>
                </span>
              )}
            </RouterLink>
            <button
              onClick={() => {
                console.log("clicked");
                tableDetailsActions.showPayment();
              }}
              class="btn btn-primary "
            >
              <span class="flex justify-center items-center">
                <span class="material-symbols-outlined mr-0.5">
                  credit_card
                </span>
                <span class="hidden 2xl:inline">
                  {t("tables.general.pay_button")}
                </span>
              </span>
            </button>
          </>
        ) : (
          <RouterLink
            class="btn btn-accent"
            to={`/app/pos?tableId=${props.tableStatus.table.id}`}
          >
            {() => (
              <span class="flex justify-center items-center">
                <span class="material-symbols-outlined mr-0.5">
                  add_shopping_cart
                </span>{" "}
              </span>
            )}
          </RouterLink>
        )}
      </div>
    );

    const renderMobileView = () => {
      const isBusy = !!props.tableStatus.table.is_busy;
      return (
        <div
          onClick={() => {
            isBottomSheetOpen.value = !isBottomSheetOpen.value;
          }}
          class="bg-base-200 rounded-lg py-2 px-2 my-2 border space-y-1"
        >
          <div class="flex justify-between items-center">
            <div class="flex items-center flex-1 space-x-2">
              <span>{getTableStatusIcon(isBusy)}</span>
              <span class="text-accent text-1.5xl font-semibold">
                {props.tableStatus.table.name}
              </span>
            </div>
            <div class="flex-1 flex justify-center items-center hidden">
              <div class={`btn ${isBusy ? "btn-error" : "btn-success"} btn-xs`}>
                {isBusy ? t("tables.general.busy") : t("tables.general.free")}
              </div>
            </div>
            <div class="flex flex-1 justify-end">
              <div
                onClick={() => {
                  isBottomSheetOpen.value = !isBottomSheetOpen.value;
                }}
              >
                {isBusy ? (
                  <div class="text-xs">
                    <TableAmountLines
                      tableStatus={props.tableStatus}
                      currency={auth.user.value?.restaurant.currency || "CHF"}
                    />
                  </div>
                ) : null}
              </div>
            </div>{" "}
          </div>
        </div>
      );
    };
    const renderDesktop = () => {
      const isBusy = !!props.tableStatus.table.is_busy;
      return (
        <tr>
          <td class="text-accent">
            <span class="flex items-center">
              {getTableStatusIcon(isBusy)}
              <span class="pl-0.5">{props.tableStatus.table.name}</span>
            </span>
          </td>
          <td class="text-accent hidden 2xl:table-cell">
            {isBusy ? (
              <RouterLink
                class="hover:underline"
                to={`/app/orders?tableId=${props.tableStatus.table.id}`}
              >
                {() => (
                  <span class="flex items-center">
                    <span>{props.tableStatus.orders.length}</span>
                    <span class="material-symbols-outlined text-1xl">
                      menu_open
                    </span>
                  </span>
                )}
              </RouterLink>
            ) : (
              "-"
            )}
          </td>
          <td class="text-accent">{isBusy ? getTimeSinceFirstOrder() : "-"}</td>
          <td class="text-accent">
            {isBusy ? (
              <TableAmountLines
                tableStatus={props.tableStatus}
                currency={auth.user.value?.restaurant.currency || "CHF"}
                align="start"
              />
            ) : (
              "-"
            )}
          </td>
          <td class="uppercase">
            <button
              class={`btn btn-primary w-24 gap-2 rounded ${
                isBusy ? "btn-error" : "btn-success"
              }`}
              disabled={isTogglingStatus.value}
              onClick={(e) => {
                e.preventDefault();
                handleToggleTableStatus();
              }}
            >
              {isTogglingStatus.value ? (
                <span class="loading loading-spinner loading-sm"></span>
              ) : isBusy ? (
                t("general.words.busy")
              ) : (
                t("general.words.free")
              )}
            </button>
          </td>
          <td class="">
            <div class="flex">
              <a
                href="#"
                class="flex justify-center items-center"
                onClick={(e) => {
                  e.preventDefault();
                  newNotes.value = currentNotes.value;
                  isNotesModalOpen.value = true;
                }}
              >
                {currentNotes.value.trim().length > 0 ? (
                  <div class="max-w-24 overflow-hidden truncate">
                    {currentNotes.value}
                  </div>
                ) : (
                  <span class="material-symbols-outlined text-2xl max-w-24">
                    edit_square
                  </span>
                )}
              </a>
            </div>
          </td>
          <td class="text-right">{renderActionButtons(isBusy)}</td>
        </tr>
      );
    };

    return () => {
      return (
        <>
          {props.view === "mobile" ? renderMobileView() : renderDesktop()}
          <div>
            {tableDetailsActions.renderModals()}
            {isNotesModalOpen.value && (
              <TableNotesModal
                onClose={() => {
                  isNotesModalOpen.value = false;
                }}
                onSuccess={(text) => {
                  currentNotes.value = text || "";
                  isNotesModalOpen.value = false;
                  props.onTablePayment && props.onTablePayment();
                }}
                tableStatus={props.tableStatus}
              />
            )}
            {isBottomSheetOpen.value && (
              <TableDetails
                isOpen={isBottomSheetOpen.value}
                onClose={() => (isBottomSheetOpen.value = false)}
                tableStatus={props.tableStatus}
                onShowPayment={() => {
                  tableDetailsActions.showPayment();
                }}
                showChangeTableModal={tableDetailsActions.showChangeTableModal}
                showTransferItemsModal={
                  tableDetailsActions.showTransferItemsModal
                }
                printReceipt={tableDetailsActions.printReceipt}
              />
            )}
          </div>
        </>
      );
    };
  },
});
